import { useMemo } from 'react';
import {
  servicesConstants,
  useFeatureFlagsContext,
  useServicesQuery,
} from '@jarvis/react-portal-addons';

const {
  SERVICE_TYPES,
  SERVICE_LEVELS,
  SERVICE_STATES,
  COMMON_QUERIES,
} = servicesConstants;

const {
  INSTANT_INK_ALL_ENABLED_QUERY,
  INSTANT_INK_ALL_OFFERING_QUERY,
} = COMMON_QUERIES;

/**
 * Returns the visibility flags for the Consolidated Dashboard cards.
 *
 * @returns {object} { showInstantInkCard: boolean, showHpAllInPlanCard: boolean, showSustainabilityCard: boolean, showGetHpAppCard: boolean }
 */
export const useCardsVisibility = ({ deviceCache }) => {
  const {
    consolidatedPortalInstantInkCard,
    consolidatedPortalHpAllInCard,
    consolidatedPortalSustainabilityCard,
    consolidatedPortalGetHpAppCard,
    grantsSupport,
  } = useFeatureFlagsContext();

  const query = useMemo(() => ({
    type: grantsSupport ? SERVICE_TYPES.GRANTS : SERVICE_TYPES.ENTITLEMENTS,
    level: SERVICE_LEVELS.ALL,
    services: [
      {
        ...INSTANT_INK_ALL_ENABLED_QUERY,
        id: 'HasInstantInk',
        states: [...INSTANT_INK_ALL_ENABLED_QUERY.states, SERVICE_STATES.DISABLED],
      },
      {
        ...INSTANT_INK_ALL_OFFERING_QUERY,
        id: 'HasInstantInkOffering',
      },
    ],
  }), [grantsSupport]);

  const {
    helpers: {
      checkService,
    },
  } = useServicesQuery(query);

  // Card visibility calculation with improved logic separation
  const cardVisibilityFlags = useMemo(() => {
    const getInstantInkCardVisibility = () => {
      if (!consolidatedPortalInstantInkCard) {
        return { hasInstantInkOffering: false, hasInstantInkEntitlement: false };
      }

      return {
        hasInstantInkEntitlement: checkService({ id: 'HasInstantInk' }),
        hasInstantInkOffering: checkService({ id: 'HasInstantInkOffering' }),
      };
    };

    const getHpAllInPlanCardVisibility = () => consolidatedPortalHpAllInCard;

    const getSustainabilityCardVisibility = () => consolidatedPortalSustainabilityCard;

    const getHpAppCardVisibility = () => consolidatedPortalGetHpAppCard && deviceCache?.data?.length === 0;

    // Pre-calculate card visibility
    const showHPAIP = getHpAllInPlanCardVisibility();
    const { hasInstantInkOffering, hasInstantInkEntitlement } = getInstantInkCardVisibility();
    const showII = hasInstantInkOffering || hasInstantInkEntitlement;
    const showS = getSustainabilityCardVisibility();
    const showGHA = getHpAppCardVisibility();

    // Minimal visibility rules enforcement: for a card to be visible, at least another one should be visible
    const showInstantInkCard = showII && (showHPAIP || showS || showGHA);
    const showHpAllInPlanCard = showHPAIP && (showII || showS || showGHA);
    const showSustainabilityCard = showS && (showII || showHPAIP || showGHA);
    const showGetHpAppCard = showGHA && (showII || showHPAIP || showS);

    return {
      showHpAllInPlanCard,
      showInstantInkCard,
      showSustainabilityCard,
      showGetHpAppCard,
      instantInkCardLinkToOverview: hasInstantInkEntitlement,
    };
  }, [
    consolidatedPortalInstantInkCard,
    checkService,
    consolidatedPortalHpAllInCard,
    consolidatedPortalSustainabilityCard,
    consolidatedPortalGetHpAppCard,
    deviceCache?.data?.length,
  ]);

  return cardVisibilityFlags;
};

export default useCardsVisibility;
